import { createRef, type FC, type HTMLAttributes, useCallback, useEffect, useMemo, useState } from 'react'
import { usePrevious } from 'react-use'
import { useTheme } from '@mui/material/styles'
import { type AutocompleteRequestUsageEnum, type FavoritePlace } from 'invictus-sdk-typescript'

import FormErrorHelper from '@Components/utils/errorHelper/FormErrorHelper'
import AutocompleteNoResults from '@DS/components/components/searchBar/atoms/autocompleteNoResults/AutocompleteNoResults'
import { type AutocompleteResultModel } from '@DS/components/navigation/search/atoms/AutocompleteResult'
import Autocomplete from '@DS/components/navigation/search/autocomplete/Autocomplete'
import { type AutocompleteBaseProps } from '@DS/components/navigation/search/autocomplete/Autocomplete.types'
import { type GenericAutocompleteProps } from '@DS/components/navigation/search/autocomplete/GenericAutocomplete'
import { type ODSearchElevation, type ODSearchOnSwap } from '@DS/components/navigation/search/common/ODSearch.types'
import ODSearch from '@DS/components/navigation/search/odSearch/ODSearch'
import ODSearchSticky from '@DS/components/navigation/search/odSearchSticky/ODSearchSticky'
import { type AutocompleteInputProps } from '@Hooks/autocomplete/useAutocomplete'
import { useSearchAutocomplete } from '@Hooks/autocomplete/useSearchAutocomplete'
import { useSearchAutocompleteSuggestions } from '@Hooks/autocomplete/useSearchAutocompleteSuggestions'

import AutocompleteFavoritePlaces, {
  type AutocompleteFavoritePlacesProps,
} from './autocompleteFavoritePlaces/AutocompleteFavoritePlaces'
import DefaultAutocompleteWrapper from './DefaultAutocompleteWrapper'
import * as styles from './styles'

const INPUT_ERROR_ID = 'od_search_controller_error'

const errorInputProps = {
  'aria-describedby': INPUT_ERROR_ID,
  'aria-invalid': true,
}

type FocusedField = 'NONE' | 'ORIGIN' | 'DESTINATION'

export type ODSearchControllerProps = {
  elevation?: ODSearchElevation
  originInputValue?: string
  destinationInputValue?: string
  AutocompleteWrapper?: FC
  isSticky?: boolean
  onClearOrigin?: VoidFunction
  onClearDestination?: VoidFunction
  originAutocompleteProps?: Partial<AutocompleteBaseProps<AutocompleteResultModel>>
  destinationAutocompleteProps?: Partial<AutocompleteBaseProps<AutocompleteResultModel>>
  originInputPlaceProps?: Partial<Omit<AutocompleteInputProps, 'defaultValue'>>
  destinationInputPlaceProps?: Partial<Omit<AutocompleteInputProps, 'defaultValue'>>
  onSwap?: ODSearchOnSwap
  onError: (error: unknown) => void
  isSwapHidden?: boolean
  hasToKeepStationsOnly?: boolean
  shouldIgnoreSuggestions?: boolean
  hasToRestoreSelectedOnBlur?: boolean
  usage?: AutocompleteRequestUsageEnum
  hasAutoFocusDisabled?: boolean
  shouldDisplayFavoritePlaces?: boolean
  hasAutocompleteWithBorder?: boolean
  recalculateAutocompleteMaxHeightAfterDelayInMs?: GenericAutocompleteProps<AutocompleteResultModel>['recalculateMaxHeightAfterDelayInMs']
  errorHelperText?: string
  onClickOnAddFavoritePlace?: AutocompleteFavoritePlacesProps['onClickOnAddFavoritePlace']
}

const ODSearchController: FC<ODSearchControllerProps & HTMLAttributes<HTMLDivElement>> = ({
  elevation,
  originInputValue,
  destinationInputValue,
  AutocompleteWrapper = DefaultAutocompleteWrapper,
  isSticky = false,
  onClearOrigin = () => {},
  onClearDestination = () => {},
  originAutocompleteProps,
  destinationAutocompleteProps,
  originInputPlaceProps: {
    inputRef: originInputPlacePropsInputRef,
    onFocus: originOnFocus = () => {},
    onBlur: originOnBlur = () => {},
    ...restOriginInputPlaceProps
  } = {},
  destinationInputPlaceProps: {
    inputRef: destinationInputPlacePropsInputRef,
    onFocus: destinationOnFocus = () => {},
    onBlur: destinationOnBlur = () => {},
    ...restDestinationInputPlaceProps
  } = {},
  onSwap,
  onError,
  isSwapHidden = false,
  hasToKeepStationsOnly = false,
  shouldIgnoreSuggestions = false,
  hasToRestoreSelectedOnBlur: hasToRestoreSelectedOnBlurProp = false,
  usage,
  hasAutoFocusDisabled = false,
  shouldDisplayFavoritePlaces = false,
  hasAutocompleteWithBorder = false,
  recalculateAutocompleteMaxHeightAfterDelayInMs = 0,
  errorHelperText,
  onClickOnAddFavoritePlace,
  ...rest
}) => {
  const theme = useTheme()

  const previousOriginInputValue = usePrevious(originInputValue)
  const previousDestinationInputValue = usePrevious(destinationInputValue)
  const [hasToRestoreSelectedOnBlur, setHasToRestoreSelectedOnBlur] = useState(hasToRestoreSelectedOnBlurProp)
  const [shouldForceOpenAutocomplete, setShouldForceOpenAutocomplete] = useState(false)
  const [isSwapped, setIsSwapped] = useState(false)
  const [isSwapping, setIsSwapping] = useState(false)

  const [focusedField, setFocusedField] = useState<FocusedField>('NONE')
  const originInputRef = originInputPlacePropsInputRef || createRef()
  const destinationInputRef = destinationInputPlacePropsInputRef || createRef()
  const suggestionsOptions = useMemo(() => ({ shouldIgnoreSuggestions }), [shouldIgnoreSuggestions])
  const {
    suggestions: originSuggestions,
    favoritePlaces,
    hasReachedMaximumPlaces,
  } = useSearchAutocompleteSuggestions('origin', suggestionsOptions)
  const { suggestions: destinationSuggestions } = useSearchAutocompleteSuggestions('destination', suggestionsOptions)

  // On swap button click, autocomplete should be kept open and input value should not be restored
  const preserveFocusOnSwapClick = (shouldOpen: boolean) => {
    setShouldForceOpenAutocomplete(shouldOpen)
    setHasToRestoreSelectedOnBlur(false)
  }

  // Clean up modified state when swap is complete (see preserveFocusOnSwapClick)
  const cleanUpAfterSwap = useCallback(() => {
    setShouldForceOpenAutocomplete(false)
    setHasToRestoreSelectedOnBlur(hasToRestoreSelectedOnBlurProp)
    setIsSwapping(false)
  }, [hasToRestoreSelectedOnBlurProp])

  const commonAutocompleteOptions = {
    onError,
    hasToAutoHighlight: true,
    hasToRestoreSelectedOnBlur,
    hasToKeepStationsOnly,
    usage,
  }

  const [
    ac1InputProps,
    ac1Props,
    { setInputValue: setAc1InputValue, clearInputValue: clearAc1InputValue },
    { hasNoResults: hasNoResultsAc1 },
  ] = useSearchAutocomplete(isSwapped ? destinationSuggestions : originSuggestions, {
    autocompleteProps: isSwapped ? destinationAutocompleteProps : originAutocompleteProps,
    inputProps: {
      defaultValue: isSwapped ? destinationInputValue : originInputValue,
      onFocus: () => {
        setFocusedField(isSwapped ? 'DESTINATION' : 'ORIGIN')
        setShouldForceOpenAutocomplete(false)

        if (isSwapped) {
          destinationOnFocus()
        } else {
          originOnFocus()
        }
      },
      onBlur: (event) => {
        const { relatedTarget } = event || {}

        const otherInputRef = isSwapped ? originInputRef : destinationInputRef

        if (relatedTarget && 'current' in otherInputRef && otherInputRef.current?.contains(relatedTarget as Node)) {
          setShouldForceOpenAutocomplete(true)
        }

        if (isSwapped) {
          destinationOnBlur()
        } else {
          originOnBlur()
        }
      },
      inputRef: isSwapped ? destinationInputRef : originInputRef,
      ...(isSwapped ? restDestinationInputPlaceProps : restOriginInputPlaceProps),
      inputProps: {
        ...(isSwapped ? restDestinationInputPlaceProps.inputProps : restOriginInputPlaceProps.inputProps),
        ...(errorHelperText ? errorInputProps : {}),
      },
    },
    ...commonAutocompleteOptions,
  })

  const [
    ac2InputProps,
    ac2Props,
    { setInputValue: setAc2InputValue, clearInputValue: clearAc2InputValue },
    { hasNoResults: hasNoResultsAc2 },
  ] = useSearchAutocomplete(isSwapped ? originSuggestions : destinationSuggestions, {
    autocompleteProps: isSwapped ? originAutocompleteProps : destinationAutocompleteProps,
    inputProps: {
      defaultValue: isSwapped ? originInputValue : destinationInputValue,
      onFocus: () => {
        setFocusedField(isSwapped ? 'ORIGIN' : 'DESTINATION')
        setShouldForceOpenAutocomplete(false)

        if (isSwapped) {
          originOnFocus()
        } else {
          destinationOnFocus()
        }
      },
      onBlur: (event) => {
        const { relatedTarget } = event || {}

        const otherInputRef = isSwapped ? destinationInputRef : originInputRef

        if (relatedTarget && 'current' in otherInputRef && otherInputRef.current?.contains(relatedTarget as Node)) {
          setShouldForceOpenAutocomplete(true)
        }

        if (isSwapped) {
          originOnBlur()
        } else {
          destinationOnBlur()
        }
      },
      inputRef: isSwapped ? originInputRef : destinationInputRef,
      ...(isSwapped ? restOriginInputPlaceProps : restDestinationInputPlaceProps),
      inputProps: {
        ...(isSwapped ? restOriginInputPlaceProps.inputProps : restDestinationInputPlaceProps.inputProps),
        ...(errorHelperText ? errorInputProps : {}),
      },
    },
    ...commonAutocompleteOptions,
  })

  // Update values when change is coming from store
  useEffect(() => {
    if (!originInputValue || originInputValue === previousOriginInputValue) {
      return
    }

    if (isSwapped) {
      setAc2InputValue(originInputValue)
    } else {
      setAc1InputValue(originInputValue)
    }
  }, [originInputValue, previousOriginInputValue, isSwapped, setAc1InputValue, setAc2InputValue])

  useEffect(() => {
    if (!destinationInputValue || destinationInputValue === previousDestinationInputValue) {
      return
    }

    if (isSwapped) {
      setAc1InputValue(destinationInputValue)
    } else {
      setAc2InputValue(destinationInputValue)
    }
  }, [destinationInputValue, previousDestinationInputValue, isSwapped, setAc1InputValue, setAc2InputValue])

  // Focus field after swap, after handlers for both fields have been swapped in DOM
  useEffect(() => {
    if (!isSwapping) {
      return
    }

    if (shouldForceOpenAutocomplete) {
      if (focusedField === 'ORIGIN' && 'current' in destinationInputRef) {
        destinationInputRef.current?.focus()
      } else if (focusedField === 'DESTINATION' && 'current' in originInputRef) {
        originInputRef.current?.focus()
      }
    }

    cleanUpAfterSwap()
  }, [shouldForceOpenAutocomplete, isSwapping, focusedField, destinationInputRef, originInputRef, cleanUpAfterSwap])

  const handleSwap: ODSearchOnSwap = (oldValues, newValues) => {
    setIsSwapping(true)
    setIsSwapped((actualIsSwapped) => !actualIsSwapped)
    onSwap?.(oldValues, newValues)
  }

  const ODSearchComponent = isSticky ? ODSearchSticky : ODSearch

  const originOnClear = () => {
    onClearOrigin()

    if ('current' in originInputRef) {
      originInputRef.current?.focus()
    }

    if (isSwapped) {
      clearAc2InputValue()
    } else {
      clearAc1InputValue()
    }
  }

  const destinationOnClear = () => {
    onClearDestination()

    if ('current' in destinationInputRef) {
      destinationInputRef.current?.focus()
    }

    if (isSwapped) {
      clearAc1InputValue()
    } else {
      clearAc2InputValue()
    }
  }

  const originInputProps = isSwapped ? ac2InputProps : ac1InputProps
  const destinationInputProps = isSwapped ? ac1InputProps : ac2InputProps

  const isAC1CurrentAutocomplete =
    (focusedField === 'ORIGIN' && ((!isSwapped && !isSwapping) || (isSwapped && isSwapping))) ||
    (focusedField === 'DESTINATION' && ((isSwapped && !isSwapping) || (!isSwapped && isSwapping)))

  const { shouldOpen: currentShouldOpen, ...currentAcProps } = isAC1CurrentAutocomplete ? ac1Props : ac2Props
  const shouldDisplayNoResults = isAC1CurrentAutocomplete ? hasNoResultsAc1 : hasNoResultsAc2
  const inputValue = isAC1CurrentAutocomplete ? ac1InputProps.value : ac2InputProps.value

  const selectFavoritePlace = ({ place }: FavoritePlace) => {
    currentAcProps.onSelectItem?.(place)
  }

  return (
    <div {...rest}>
      <ODSearchComponent
        css={errorHelperText ? styles.error(theme) : undefined}
        elevation={elevation}
        originInput={{
          autoFocus: !hasAutoFocusDisabled && originInputValue === undefined,
          onClear: originOnClear,
          ...originInputProps,
        }}
        destinationInput={{
          autoFocus: destinationInputValue === undefined && originInputValue !== undefined,
          onClear: destinationOnClear,
          ...destinationInputProps,
        }}
        onSwap={isSwapHidden ? undefined : handleSwap}
        swapProps={{ onMouseDown: () => preserveFocusOnSwapClick(currentShouldOpen) }}
      />
      {errorHelperText && (
        <div css={styles.errorMessageWrapper(theme)}>
          <FormErrorHelper id={INPUT_ERROR_ID} helperText={errorHelperText} error={!!errorHelperText} />
        </div>
      )}
      {focusedField !== 'NONE' && (
        <AutocompleteWrapper>
          <Autocomplete
            css={[
              styles.autocomplete(theme),
              (elevation === 'onSurface' || hasAutocompleteWithBorder) && styles.autocompleteWithBorder(theme),
            ]}
            {...currentAcProps}
            recalculateMaxHeightAfterDelayInMs={recalculateAutocompleteMaxHeightAfterDelayInMs}
            shouldOpen={shouldForceOpenAutocomplete || currentShouldOpen}
          >
            {shouldDisplayFavoritePlaces && (
              <AutocompleteFavoritePlaces
                favoritePlaces={favoritePlaces}
                hasReachedMaximumPlaces={hasReachedMaximumPlaces}
                onSelect={selectFavoritePlace}
                onClickOnAddFavoritePlace={onClickOnAddFavoritePlace}
              />
            )}
            {shouldDisplayNoResults && <AutocompleteNoResults inputValue={inputValue} />}
          </Autocomplete>
        </AutocompleteWrapper>
      )}
    </div>
  )
}

export default ODSearchController
